/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import flatten from "lodash/flatten";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import { SelectableOptionMenuItemType } from "office-ui-fabric-react/lib/SelectableOption";

import { AppState } from "Dataflows/Reducers";
import { ITechnologyAreaDataState } from "Interfaces/SiteContent/ITechnologyAreaDataState";
import { ITechnologyAreaModelData } from "Interfaces/SiteContent/ITechnologyAreaModelData";
import { ITechnologyAreaOptionList } from "Interfaces/SiteContent/ITechnologyAreaOptionList";

const getTechAreas = (data: ITechnologyAreaModelData[]): ITechnologyAreaOptionList[] => {
  const techAreas = map(groupBy(data, "technologyFocusAreaGroupName"), (value, key) => {
    const header = {
      text: key,
      key: key + "-header",
      itemType: SelectableOptionMenuItemType.Header,
    };

    const group = value.map((ta) => {
      return {
        text: ta.technologyName,
        key: ta.technologyName,
        itemType: SelectableOptionMenuItemType.Normal,
      };
    });

    const divider = {
      key: key + "-divider",
      text: "-",
      itemType: SelectableOptionMenuItemType.Divider,
    };

    return [header, ...group, divider];
  });

  return flatten(techAreas);
};

/**
 * Technology area option list data selector.
 */
export const getAllTechnologyAreaOptionList = (state: AppState): ITechnologyAreaOptionList[] => {
  return getTechAreas(state.technologyAreaDataState.technologyAreaData);
};

/**
 * Technology area option list data selector.
 */
export const getTechnologyAreaOptionList = (
  state: ITechnologyAreaDataState
): ITechnologyAreaOptionList[] => {
  return getTechAreas(state.technologyAreaData);
};

/**
 * Technology area data load error selector.
 */
export const getTechnologyAreaLoadError = (state: ITechnologyAreaDataState): string =>
  state.technologyAreaLoadError;

/**
 * Technology area group option list data selector.
 */
export const getTechnologyAreaGroupOptionList = (
  state: ITechnologyAreaDataState
): ITechnologyAreaOptionList[] =>
  map(groupBy(state.technologyAreaData, "technologyFocusAreaGroupName"), (value, key) => {
    const groups = {
      text: key,
      key: key,
      itemType: SelectableOptionMenuItemType.Normal,
    };

    return groups;
  });

export const selectTechnologyAreaBySelectedGroups = (
  state: ITechnologyAreaDataState,
  selectedGroups: string[]
): ITechnologyAreaOptionList[] => {
  let techAreas: ITechnologyAreaOptionList[] = [];
  if (selectedGroups.length > 0) {
    techAreas = getTechAreas(
      state.technologyAreaData.filter((ta) => {
        return selectedGroups.includes(ta.technologyFocusAreaGroupName);
      })
    );
  } else {
    techAreas = getTechAreas(state.technologyAreaData);
  }
  return techAreas;
};
